import { useState } from "react";
import { Search, CheckCircle, AlertCircle, Loader2, AlertTriangle } from "lucide-react";
import { getSupabase } from "../../integrations/supabase/client";
const InspectionAddressForm = ({ formData, handleFormChange, validationErrors, isFromUs }) => {
  const isUS = Number(isFromUs ?? 1) === 1;
  const stateLabel = isUS ? "State*" : "Province*";
  const zipLabel = isUS ? "ZIP Code*" : "Postal Code*";
  const [addressValidated, setAddressValidated] = useState(null);
  const [hasMismatch, setHasMismatch] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [correctedAddress, setCorrectedAddress] = useState(null);
  const handleAddressLookup = async () => {
    if (!formData.address || !formData.city || !formData.state || !formData.zipCode) {
      setAddressValidated(false);
      setHasMismatch(false);
      setValidationMessage(
        `Please fill in address, city, ${isUS ? "state" : "province"}, and ${isUS ? "ZIP code" : "postal code"} to validate`
      );
      return;
    }
    setIsValidating(true);
    setAddressValidated(null);
    setHasMismatch(false);
    setValidationMessage("");
    setCorrectedAddress(null);
    try {
      const { data, error } = await getSupabase().functions.invoke("validate-address", {
        body: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
      });
      if (error) {
        console.error("Address validation error:", error);
        setAddressValidated(false);
        setValidationMessage("Unable to validate address. Please check and try again.");
        return;
      }
      const result = data;
      if (result.valid && result.addressComponents) {
        const components = result.addressComponents;
        const userState = formData.state.toUpperCase().trim();
        const returnedState = components.state.toUpperCase().trim();
        const userCity = formData.city.toUpperCase().trim();
        const returnedCity = components.city.toUpperCase().trim();
        const stateMismatch = userState !== returnedState;
        const cityMismatch = userCity !== returnedCity;
        if (stateMismatch || cityMismatch) {
          setAddressValidated(true);
          setHasMismatch(true);
          setCorrectedAddress(components);
          setValidationMessage(`Address found but differs from entered: ${result.matchedAddress}`);
        } else {
          setAddressValidated(true);
          setHasMismatch(false);
          setValidationMessage(result.matchedAddress || "Address validated successfully");
        }
      } else if (result.valid) {
        setAddressValidated(true);
        setValidationMessage(result.matchedAddress || "Address validated successfully");
      } else {
        setAddressValidated(false);
        setValidationMessage(result.message || result.error || "Address not found");
      }
    } catch (err) {
      console.error("Address lookup failed:", err);
      setAddressValidated(false);
      setValidationMessage("Unable to validate address at this time");
    } finally {
      setIsValidating(false);
    }
  };
  const handleUseCorrectedAddress = () => {
    if (correctedAddress) {
      handleFormChange("address", correctedAddress.streetAddress);
      handleFormChange("city", correctedAddress.city);
      handleFormChange("state", correctedAddress.state);
      handleFormChange("zipCode", correctedAddress.zipCode);
      setHasMismatch(false);
      setCorrectedAddress(null);
      setValidationMessage(
        `Address updated to: ${correctedAddress.streetAddress}, ${correctedAddress.city}, ${correctedAddress.state} ${correctedAddress.zipCode}`
      );
    }
  };
  const inputClassName =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-[#239D80] focus:border-[#239D80] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#239D80] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
  return (
    <div className="p-6 py-0">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Where is the inspection located?</h2>
      <div className="space-y-4">
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
            Street Address*
          </label>
          <div className="flex gap-2">
            <input
              id="address"
              value={formData.address}
              onChange={(e) => {
                handleFormChange("address", e.target.value);
                setAddressValidated(null);
              }}
              className={`${inputClassName} flex-1`}
              placeholder="123 Main St"
              maxLength={30}
            />
            <button
              type="button"
              onClick={handleAddressLookup}
              disabled={isValidating || !formData.address || !formData.city || !formData.state || !formData.zipCode}
              className="px-4 h-10 bg-[#239d80] text-white rounded-md hover:bg-[#1e8a6f] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {isValidating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {isValidating ? "Validating..." : "Validate"}
            </button>
          </div>
          {validationErrors.address && (
            <p className="mt-1 text-[#ea2206] text-xs py-0 my-0 px-[5px]">
              {validationErrors.address}
            </p>
          )}
          {addressValidated === true && !hasMismatch && (
            <div className="mt-1 text-[#239d80] text-xs flex items-start gap-1 px-[5px]">
              <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{validationMessage}</span>
            </div>
          )}
          {addressValidated === true && hasMismatch && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <div className="text-amber-700 text-xs flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{validationMessage}</span>
              </div>
              <button
                type="button"
                onClick={handleUseCorrectedAddress}
                className="mt-2 text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
              >
                Use Corrected Address
              </button>
            </div>
          )}
          {addressValidated === false && (
            <div className="mt-1 text-[#ea2206] text-xs flex items-start gap-1 px-[5px]">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{validationMessage}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City*
            </label>
            <input
              id="city"
              value={formData.city}
              onChange={(e) => {
                handleFormChange("city", e.target.value);
                setAddressValidated(null);
              }}
              className={inputClassName}
              placeholder="Anytown"
              maxLength={25}
            />
            {validationErrors.city && (
              <p className="mt-1 text-[#ea2206] text-xs px-[5px] py-0 my-0">
                {validationErrors.city}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
              {stateLabel}
            </label>
            <input
              id="state"
              value={formData.state}
              onChange={(e) => {
                handleFormChange("state", e.target.value);
                setAddressValidated(null);
              }}
              className={inputClassName}
              placeholder={isUS ? "CA" : "ON"}
            />
            {validationErrors.state && (
              <p className="mt-1 text-xs text-[#ea2206] px-[5px] my-0">{validationErrors.state}</p>
            )}
          </div>
        </div>
        <div className="mb-4">
          <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
            {zipLabel}
          </label>
          <input
            id="zipCode"
            value={formData.zipCode}
            onChange={(e) => {
              handleFormChange("zipCode", e.target.value);
              setAddressValidated(null);
            }}
            className={`${inputClassName} mb-[10px]`}
            placeholder={isUS ? "12345" : "A1A 1A1"}
          />
          {validationErrors.zipCode && (
            <p className="mt-1 text-xs text-[#ea2206] px-[5px] py-0 my-0">
              {validationErrors.zipCode}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
export default InspectionAddressForm;
